import { useEffect, useMemo, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Image as ImageIcon, Mic, Send, Loader2, Square, Gauge, AudioLines,
  RotateCcw, Pause, Play, X, Plus, ArrowUp, Camera, Maximize2, Sparkles, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { splitMath, extractSvgs } from "@/components/MathText";
import { MarkdownMath } from "@/components/MarkdownMath";

export function StatusPill({ kind }) {
  const map = {
    transcribing: { label: "Listening…", icon: Mic },
    thinking:     { label: "Thinking…",  icon: Loader2 },
  };
  const { label, icon: Icon } = map[kind] || map.thinking;
  return (
    <div className="flex items-center gap-2 text-slate-500 text-xs" data-testid={`tchat-status-${kind}`}>
      <Icon className={`h-3.5 w-3.5 ${kind === "thinking" ? "animate-spin" : ""}`} /> {label}
    </div>
  );
}

export function Bubble({ role, content, image, ttsUrl, picUrl, videoUrl, videoLessonId, audioLessonId, isLast, audioRef, onAudioEnded, speed, teacherName, onRedraw, onQuiz, sketchActionsDisabled }) {
  const isUser = role === "user";
  const hasVideo = !isUser && (videoLessonId || videoUrl);
  // Extract any AI-drawn SVG figures so they render as diagrams (not raw
  // markup) and don't pollute the karaoke text or the TTS transcript.
  const { text: cleanText, svgs } = useMemo(() => (isUser ? { text: content || "", svgs: [] } : extractSvgs(content || "")), [content, isUser]);
  // Tokenize the assistant text into paragraphs of word-tokens with cumulative
  // char positions. Word-level mapping makes the karaoke highlight follow the
  // audio fluidly, even when the lesson reply is long or has few full stops.
  const { paragraphs, totalChars } = useMemo(() => tokenizeForKaraoke(cleanText || ""), [cleanText]);
  // The "end char" of the currently-spoken word. We mark a word active when
  // its `end` equals this value; words with smaller `end` are "past".
  const [activeEnd, setActiveEnd] = useState(0);
  // Whether the tutor audio is currently audible — drives the "speaking now"
  // indicator above the bubble. Toggled by the <audio> element events.
  const [isSpeaking, setIsSpeaking] = useState(false);
  // Becomes true the first time audio plays; we use this to decide whether
  // to render the playback control pill (replay/pause/stop). Controls remain
  // available after the audio finishes so the student can replay.
  const [hasStarted, setHasStarted] = useState(false);

  // Streamed TTS responses (Sarvam / OpenAI fallback) ship without a
  // Content-Length, so `audio.duration` reports `Infinity` for most of the
  // playback and only becomes finite at the very end. The previous logic
  // gated on `isFinite(duration)`, which meant ~95% of timeupdate events
  // were ignored and only the last 4-5 words ever lit up. We now use the
  // real duration when it is available and otherwise fall back to a
  // character-rate estimate driven by `currentTime` alone.
  const CHARS_PER_SEC = 14; // ~conversational TTS pace; works for EN & KN

  const onTimeUpdate = (e) => {
    if (isUser || !isLast || !paragraphs.length) return;
    const a = e.currentTarget;
    const t = a.currentTime || 0;
    const dur = a.duration;
    const rate = a.playbackRate || 1;
    let target;
    if (dur && isFinite(dur) && dur > 0) {
      target = (t / dur) * totalChars;
    } else {
      // Heuristic: spoken-chars-per-second scaled by current playback rate.
      target = t * CHARS_PER_SEC * rate;
    }
    if (target > totalChars) target = totalChars;
    // Find the first word whose `end` exceeds the target time-cursor; that's
    // the word currently being spoken. Fall back to the last word at the tail.
    let nextEnd = paragraphs[paragraphs.length - 1].slice(-1)[0]?.end || 0;
    outer: for (const words of paragraphs) {
      for (const w of words) {
        if (w.end > target) { nextEnd = w.end; break outer; }
      }
    }
    if (nextEnd !== activeEnd) setActiveEnd(nextEnd);
  };

  const showControls = !isUser && isLast && ttsUrl && hasStarted;

  // Imperative audio actions — driven by the same <audio> the bubble owns.
  const handleReplay = () => {
    const el = audioRef?.current;
    if (!el) return;
    try {
      el.currentTime = 0;
      setActiveEnd(0);
      const p = el.play();
      if (p && typeof p.then === "function") p.catch(() => {/* ignore */});
    } catch {/* noop */}
  };
  const handlePauseToggle = () => {
    const el = audioRef?.current;
    if (!el) return;
    try {
      if (el.paused) {
        const p = el.play();
        if (p && typeof p.then === "function") p.catch(() => {/* ignore */});
      } else {
        el.pause();
      }
    } catch {/* noop */}
  };
  const handleStop = () => {
    const el = audioRef?.current;
    if (!el) return;
    try {
      el.pause();
      el.currentTime = 0;
      // Snap highlight to the last word so the student knows it's over.
      const last = paragraphs[paragraphs.length - 1]?.slice(-1)[0];
      if (last) setActiveEnd(last.end);
    } catch {/* noop */}
  };

  // Bind this bubble to the shared, gesture-primed page audio element. Using
  // ONE persistent element (unlocked on the student's first tap) is what makes
  // playback start reliably on phones — a fresh per-bubble <audio> created
  // seconds after the tap is blocked by mobile autoplay policies.
  useEffect(() => {
    const el = audioRef?.current;
    if (isUser || !isLast || !ttsUrl || !el) return;
    let cancelled = false;
    let retried = false;
    setActiveEnd(0);
    const onPlay = () => { setIsSpeaking(true); setHasStarted(true); };
    const onPause = () => setIsSpeaking(false);
    const onEnded = (e) => {
      const last = paragraphs[paragraphs.length - 1]?.slice(-1)[0];
      if (last) setActiveEnd(last.end);
      setIsSpeaking(false);
      onAudioEnded?.(e);
    };
    const onErr = (e) => {
      // Auto-reconnect once (network blip / cold stream) before giving up.
      if (!retried && !cancelled) {
        retried = true;
        try {
          el.src = ttsUrl + (ttsUrl.includes("?") ? "&" : "?") + "r=" + Date.now();
          el.load();
          const rp = el.play();
          if (rp && rp.catch) rp.catch(() => {});
        } catch {/* noop */}
        return;
      }
      setIsSpeaking(false);
      onAudioEnded?.(e);
    };
    const onTU = () => onTimeUpdate({ currentTarget: el });
    const onLM = () => { if (speed) { try { el.playbackRate = speed; } catch {/* noop */} } };
    el.addEventListener("play", onPlay);
    el.addEventListener("playing", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("error", onErr);
    el.addEventListener("timeupdate", onTU);
    el.addEventListener("loadedmetadata", onLM);
    // Load this reply's audio and start it (element already unlocked by a tap).
    try {
      if (el.src !== ttsUrl) { el.src = ttsUrl; el.load(); }
      if (speed) el.playbackRate = speed;
      const p = el.play();
      if (p && p.catch) p.catch(() => { if (!cancelled) setIsSpeaking(false); });
    } catch {/* noop */}
    return () => {
      cancelled = true;
      el.removeEventListener("play", onPlay);
      el.removeEventListener("playing", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("error", onErr);
      el.removeEventListener("timeupdate", onTU);
      el.removeEventListener("loadedmetadata", onLM);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsUrl, isLast, audioRef]);

  return (
    <div className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}>
      {showControls && (
        <div
          className="mb-1.5 ml-1 inline-flex items-center gap-1.5 rounded-full bg-sky-50 dark:bg-sky-900/40 border border-sky-200/70 dark:border-sky-700/60 pl-2.5 pr-1 py-1 text-[11px] font-semibold text-sky-700 dark:text-sky-200"
          data-testid="tchat-speaking-indicator"
        >
          {isSpeaking ? (
            <>
              <AudioLines className="h-3.5 w-3.5 text-sky-500 animate-pulse" />
              <span>
                {teacherName ? `${teacherName} teacher speaking…` : "Tutor speaking…"}
              </span>
            </>
          ) : (
            <span className="text-slate-500 dark:text-slate-400 pl-0.5">
              {teacherName ? `${teacherName} teacher · paused` : "Paused"}
            </span>
          )}
          <div className="flex items-center gap-0.5 ml-1">
            <button
              type="button"
              onClick={handleReplay}
              title="Replay from the beginning"
              className="h-6 w-6 rounded-full bg-white/70 dark:bg-slate-800/60 hover:bg-white dark:hover:bg-slate-700 text-sky-700 dark:text-sky-200 flex items-center justify-center transition-colors"
              data-testid="tchat-replay-btn"
              aria-label="Replay"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={handlePauseToggle}
              title={isSpeaking ? "Pause" : "Resume"}
              className="h-6 w-6 rounded-full bg-white/70 dark:bg-slate-800/60 hover:bg-white dark:hover:bg-slate-700 text-sky-700 dark:text-sky-200 flex items-center justify-center transition-colors"
              data-testid="tchat-pause-btn"
              aria-label={isSpeaking ? "Pause" : "Resume"}
            >
              {isSpeaking ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            </button>
            <button
              type="button"
              onClick={handleStop}
              title="Stop playback"
              className="h-6 w-6 rounded-full bg-white/70 dark:bg-slate-800/60 hover:bg-white dark:hover:bg-slate-700 text-rose-600 dark:text-rose-300 flex items-center justify-center transition-colors"
              data-testid="tchat-stop-btn"
              aria-label="Stop"
            >
              <Square className="h-3 w-3 fill-current" />
            </button>
          </div>
        </div>
      )}
      <div className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`tchat-bubble-${role}`}
        className={
          "rounded-2xl px-4 py-3 text-sm leading-relaxed " +
          (hasVideo ? "w-full max-w-[92%] sm:max-w-[640px] " : "max-w-[85%] ") +
          (isUser
            ? "bg-sky-500 text-white rounded-br-sm whitespace-pre-wrap"
            : "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-bl-sm")
        }
      >
        {isUser ? (
          <>
            {image && <img src={image} alt="attachment" className="mb-2 max-h-48 rounded-xl object-cover" data-testid="tchat-user-image" />}
            {content}
          </>
        ) : (
          <div className="space-y-2" data-testid="tchat-assistant-text">
            <MarkdownMath content={cleanText} />
          </div>
        )}
        {!isUser && svgs.length > 0 && (
          <div className="mt-2 space-y-2" data-testid="tchat-figures">
            {svgs.map((svg, si) => (
              <div
                key={si}
                className="block w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white p-2 [&_svg]:block [&_svg]:w-full [&_svg]:h-auto"
                data-testid={`tchat-figure-${si}`}
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            ))}
            {isLast && (onRedraw || onQuiz) && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="tchat-sketch-actions">
                {onRedraw && (
                  <>
                    <button type="button" disabled={sketchActionsDisabled} onClick={() => onRedraw("bigger")} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-50 transition-colors" data-testid="sketch-redraw-bigger">
                      <Maximize2 className="h-3.5 w-3.5" /> Bigger
                    </button>
                    <button type="button" disabled={sketchActionsDisabled} onClick={() => onRedraw("simpler")} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-50 transition-colors" data-testid="sketch-redraw-simpler">
                      <Sparkles className="h-3.5 w-3.5" /> Simpler
                    </button>
                  </>
                )}
                {onQuiz && (
                  <button type="button" disabled={sketchActionsDisabled} onClick={() => onQuiz()} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 disabled:opacity-50 transition-colors" data-testid="sketch-quiz-btn">
                    <ClipboardList className="h-3.5 w-3.5" /> Label the parts
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {!isUser && picUrl && (
          <img
            src={picUrl}
            alt="Illustration"
            className="mt-2 rounded-xl border border-slate-200 dark:border-slate-700 max-w-full"
            data-testid="tchat-picture-img"
          />
        )}
        {!isUser && videoLessonId && <LessonVideo lessonId={videoLessonId} />}
        {!isUser && audioLessonId && <LessonAudio lessonId={audioLessonId} />}
        {!isUser && videoUrl && <VideoEmbed url={videoUrl} />}
      </div>
      </div>
    </div>
  );
}

/** Tokenise tutor text into paragraphs of word-tokens with cumulative char
 *  positions. Paragraphs come from `\n\n` (blank-line) breaks; if the LLM
 *  forgets to split, we fall back to a single-paragraph render.
 *
 *  Returns: { paragraphs: [[{text, end}, ...], ...], totalChars: number } */
function tokenizeForKaraoke(text) {
  if (!text || !text.trim()) return { paragraphs: [], totalChars: 1 };
  // First normalise: collapse 3+ newlines to exactly 2, then split on \n{2,}
  const norm = text.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  let paras = norm.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  // Safety net: if the model returned a single huge block, soft-split it on
  // sentence terminators so highlighting still feels granular.
  if (paras.length === 1 && paras[0].length > 220) {
    const m = paras[0].match(/[^.!?]+[.!?]+\s*/g);
    if (m && m.length > 1) {
      // group ~2 sentences per visual paragraph
      const grouped = [];
      for (let i = 0; i < m.length; i += 2) {
        grouped.push((m[i] + (m[i + 1] || "")).trim());
      }
      paras = grouped.filter(Boolean);
    }
  }
  let pos = 0;
  const paragraphs = paras.map((p) => {
    const tokens = [];
    for (const seg of splitMath(p)) {
      if (seg.t === "math") {
        pos += (seg.raw || "").length;
        tokens.push({ type: "math", raw: seg.raw, end: pos });
      } else {
        const toks = seg.v.match(/\S+\s*/g) || [seg.v];
        for (const t of toks) {
          pos += t.length;
          tokens.push({ type: "word", text: t, end: pos });
        }
      }
    }
    return tokens.length ? tokens : [{ type: "word", text: p, end: (pos += p.length) }];
  });
  return { paragraphs, totalChars: Math.max(pos, 1) };
}

/**
 * LessonVideo — plays a lesson video uploaded to our own storage (Dev-Admin).
 * Fetches a short-lived signed token, then streams it from the backend with a
 * native player. Download is blocked (controlsList=nodownload, no right-click,
 * backend Sec-Fetch-Dest guard) so the raw file is never a downloadable link.
 */
export function LessonVideo({ lessonId }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/tuition/lessons/${lessonId}/video-token`);
        if (!alive) return;
        setSrc(`${API_BASE}/tuition/lessons/${lessonId}/video?t=${encodeURIComponent(data.token)}`);
      } catch {
        if (alive) setErr(true);
      }
    })();
    return () => { alive = false; };
  }, [lessonId]);

  if (err) {
    return (
      <div
        className="mt-2 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300 px-4 py-3 text-sm"
        data-testid="tchat-video-error"
      >
        This video isn't available right now. Please try again in a moment.
      </div>
    );
  }
  if (!src) {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-slate-500" data-testid="tchat-video-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading video…
      </div>
    );
  }
  return (
    <video
      src={src}
      controls
      playsInline
      preload="metadata"
      controlsList="nodownload noplaybackrate noremoteplayback"
      disablePictureInPicture
      onContextMenu={(e) => e.preventDefault()}
      className="mt-2 rounded-xl border border-slate-200 dark:border-slate-700 max-w-full w-full max-h-[420px] bg-black"
      data-testid="tchat-lesson-video"
    >
      Your browser does not support inline video.
    </video>
  );
}

/**
 * LessonAudio — plays the podcast MP3 the school admin uploaded for a lesson.
 * Same signed-token flow as LessonVideo, download blocked, no right-click.
 */
export function LessonAudio({ lessonId }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/tuition/lessons/${lessonId}/audio-token`);
        if (!alive) return;
        setSrc(`${API_BASE}/tuition/lessons/${lessonId}/audio?t=${encodeURIComponent(data.token)}`);
      } catch {
        if (alive) setErr(true);
      }
    })();
    return () => { alive = false; };
  }, [lessonId]);

  if (err) {
    return (
      <div
        className="mt-2 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300 px-4 py-3 text-sm"
        data-testid="tchat-audio-error"
      >
        This audio podcast isn't available right now. Please ask your school admin to upload it.
      </div>
    );
  }
  if (!src) {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-slate-500" data-testid="tchat-audio-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading podcast…
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-3 py-2 flex items-center gap-2" data-testid="tchat-lesson-audio-wrap">
      <span className="text-xs font-semibold text-violet-600 dark:text-violet-300 uppercase tracking-wide">Podcast</span>
      <audio
        src={src}
        controls
        preload="metadata"
        controlsList="nodownload noplaybackrate noremoteplayback"
        onContextMenu={(e) => e.preventDefault()}
        className="flex-1 min-w-0"
        data-testid="tchat-lesson-audio"
      >
        Your browser does not support inline audio.
      </audio>
    </div>
  );
}

/**
 * VideoEmbed — renders a lesson video from an arbitrary URL a teacher pasted
 * into the Tuition Library. Direct media files (.mp4/.webm/…) use a native
 * <video> player; Google Drive / YouTube / Vimeo share links are converted to
 * their embeddable /preview or /embed form and shown inside an <iframe>.
 */
export function VideoEmbed({ url }) {
  const embed = useMemo(() => resolveVideoEmbed(url), [url]);
  if (!embed) return null;

  if (embed.kind === "file") {
    return (
      <video
        src={embed.src}
        controls
        playsInline
        preload="metadata"
        className="mt-2 rounded-xl border border-slate-200 dark:border-slate-700 max-w-full w-full max-h-[420px] bg-black"
        data-testid="tchat-video"
      >
        Your browser does not support inline video.
      </video>
    );
  }

  return (
    <div
      className="mt-2 relative w-full overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-black"
      style={{ aspectRatio: "16 / 9" }}
      data-testid="tchat-video"
    >
      <iframe
        src={embed.src}
        title="Lesson video"
        className="absolute inset-0 h-full w-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
        data-testid="tchat-video-iframe"
      />
    </div>
  );
}

/** Detect the source type of a pasted video URL and return an embeddable src. */
function resolveVideoEmbed(url) {
  if (!url || typeof url !== "string") return null;
  const raw = url.trim();
  if (!raw) return null;

  // Google Drive: .../file/d/<id>/view  OR  ...open?id=<id>
  const gdrive = raw.match(/drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?id=)([-\w]{10,})/);
  if (gdrive) {
    return { kind: "iframe", src: `https://drive.google.com/file/d/${gdrive[1]}/preview` };
  }

  // YouTube: watch?v=, youtu.be/, /embed/, /shorts/
  const yt = raw.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([-\w]{6,})/);
  if (yt) {
    return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1&playsinline=1` };
  }

  // Vimeo: vimeo.com/<id>
  const vimeo = raw.match(/vimeo\.com\/(?:video\/)?(\d{6,})/);
  if (vimeo) {
    return { kind: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}` };
  }

  // Direct media file (with or without query string).
  if (/\.(mp4|webm|ogg|ogv|mov|m4v)(\?.*)?$/i.test(raw)) {
    return { kind: "file", src: raw };
  }

  // Fallback: assume it's directly playable media.
  return { kind: "file", src: raw };
}

// Unified ChatGPT-style composer: one pill with attach (+), text input, a mic
// (tap to talk) and an adaptive primary button (send when typing / stop while
// recording / start-voice when idle). Replaces the separate big-mic voice UI
// and the chat/voice header toggle.
export function UnifiedComposer({
  value, onChange, onSend, busy,
  image, onImage, onClearImage,
  recording, level, onStartRec, onStopRec,
}) {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const hasText = !!value.trim();

  // Downscale any picked/pasted/captured image to keep it small & fast for the
  // vision model (big phone photos and PNG screenshots were being rejected).
  async function toScaledDataUrl(file, maxDim = 1600, quality = 0.85) {
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    try {
      const img = await new Promise((res, rej) => {
        const im = new window.Image();
        im.onload = () => res(im);
        im.onerror = rej;
        im.src = dataUrl;
      });
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      return canvas.toDataURL("image/jpeg", quality);
    } catch {
      return dataUrl; // fall back to the original if canvas fails
    }
  }

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (hasText || image) onSend();
    }
  };
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    setAttachOpen(false);
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 25 * 1024 * 1024) { toast.error("Image too large. Max 25 MB."); return; }
    try { onImage?.(await toScaledDataUrl(f)); }
    catch { toast.error("Could not read that image."); }
  };
  // Paste a snippet: students screenshot/snip their textbook and Ctrl+V into chat.
  const onPaste = async (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        const f = it.getAsFile?.();
        if (f) {
          e.preventDefault();
          try { onImage?.(await toScaledDataUrl(f)); toast.success("Snippet pasted — type your question and send."); }
          catch { toast.error("Could not paste that image."); }
          return;
        }
      }
    }
  };

  const ringScale = 1 + Math.min(level || 0, 1) * 0.4;

  return (
    <div data-testid="tchat-unified-composer">
      {image && (
        <div className="mb-2 inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 p-1.5 bg-white dark:bg-slate-800" data-testid="tchat-image-preview">
          <img src={image} alt="attachment" className="h-12 w-12 rounded-lg object-cover" />
          <button type="button" onClick={onClearImage} className="text-slate-400 hover:text-rose-500 pr-1" data-testid="tchat-image-remove"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 pl-2 pr-2 py-1.5 shadow-sm focus-within:border-sky-400 dark:focus-within:border-sky-500 transition-colors">
        {/* Attach (+) — choose from library or take a photo.
            Uses native <label htmlFor> triggers so the file/camera dialog
            opens reliably on all devices (no programmatic .click() gesture issues). */}
        <input id="tchat-file-lib" ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="tchat-image-input" />
        <input id="tchat-file-cam" ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid="tchat-camera-input" />
        <Popover open={attachOpen} onOpenChange={setAttachOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={busy}
              className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors"
              title="Attach a photo"
              data-testid="tchat-attach"
            >
              <Plus className="h-5 w-5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" side="top" className="w-48 p-1">
            <label
              htmlFor="tchat-file-cam"
              className="flex items-center gap-2 px-2.5 py-2 rounded-md text-sm cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid="tchat-attach-camera"
            >
              <Camera className="h-4 w-4" /> Take photo
            </label>
            <label
              htmlFor="tchat-file-lib"
              className="flex items-center gap-2 px-2.5 py-2 rounded-md text-sm cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
              data-testid="tchat-attach-library"
            >
              <ImageIcon className="h-4 w-4" /> Choose photo
            </label>
            <div className="px-2.5 py-2 text-xs text-slate-400 border-t border-slate-100 dark:border-slate-700 mt-1" data-testid="tchat-attach-paste-hint">
              Tip: snip/screenshot your textbook and press <b>Ctrl/⌘+V</b> here to paste it.
            </div>
          </PopoverContent>
        </Popover>

        {/* Text input */}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={recording ? "Listening… tap ■ to send" : "Ask your doubt… or paste a snippet"}
          disabled={busy || recording}
          className="flex-1 min-w-0 bg-transparent outline-none text-[15px] text-slate-800 dark:text-slate-100 placeholder:text-slate-400 px-1"
          data-testid="tchat-input"
        />

        {/* Screenshot / photo — attach an image right here and ask about it.
            Uses <label htmlFor> so the device photo picker (gallery incl. your
            screenshots, or camera) opens reliably on phones, tablets & desktop. */}
        <label
          htmlFor="tchat-file-lib"
          title="Attach a screenshot or photo"
          aria-label="Attach a screenshot or photo"
          className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center cursor-pointer text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${busy ? "opacity-40 pointer-events-none" : ""}`}
          data-testid="tchat-screenshot-btn"
        >
          <ImageIcon className="h-5 w-5" />
        </label>

        {/* Mic (tap to talk) — hidden while typing */}
        {!hasText && (
          <button
            type="button"
            onClick={recording ? onStopRec : onStartRec}
            disabled={busy}
            className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${
              recording ? "text-rose-500" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            }`}
            title={recording ? "Stop" : "Tap to speak"}
            data-testid="tchat-mic-btn"
            aria-label={recording ? "Stop recording" : "Tap to speak"}
          >
            <Mic className="h-5 w-5" />
          </button>
        )}

        {/* Adaptive primary button */}
        <div className="relative shrink-0">
          {recording && (
            <span
              className="absolute inset-0 rounded-full bg-rose-400/40 -z-10 transition-transform duration-100"
              style={{ transform: `scale(${ringScale})` }}
              data-testid="tchat-mic-waveform"
            />
          )}
          <button
            type="button"
            onClick={() => {
              if (recording) { onStopRec(); return; }
              if (hasText || image) { onSend(); return; }
              onStartRec();
            }}
            disabled={busy && !recording}
            className={`h-10 w-10 rounded-full flex items-center justify-center transition-all active:scale-95 ${
              recording
                ? "bg-rose-500 text-white"
                : hasText || image
                ? "bg-sky-500 hover:bg-sky-600 text-white"
                : "bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:opacity-90"
            }`}
            data-testid="tchat-send"
            aria-label={recording ? "Stop and send" : hasText ? "Send" : "Start voice"}
          >
            {busy && !recording ? <Loader2 className="h-4 w-4 animate-spin" />
              : recording ? <Square className="h-4 w-4" />
              : hasText || image ? <ArrowUp className="h-5 w-5" />
              : <AudioLines className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {recording && (
        <div className="text-xs text-rose-500 text-center mt-1.5" data-testid="tchat-recording-indicator">
          Listening… tap the stop button to send
        </div>
      )}
    </div>
  );
}

export function VoiceComposer({ recording, level, disabled, onStart, onStop }) {
  // Press-and-hold via pointer events (covers mouse + touch).
  const onDown = (e) => { e.preventDefault(); if (!disabled) onStart(); };
  const onUp = (e) => { e.preventDefault(); if (recording) onStop(); };
  // Scale ring with mic level
  const ringScale = 1 + Math.min(level, 1) * 0.35;
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        {recording && (
          <span
            className="absolute inset-0 rounded-full bg-sky-300/50 dark:bg-sky-400/30 -z-10 transition-transform duration-100"
            style={{ transform: `scale(${ringScale})` }}
            data-testid="tchat-mic-waveform"
          />
        )}
        <button
          type="button"
          onPointerDown={onDown}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onPointerLeave={recording ? onUp : undefined}
          disabled={disabled}
          className={
            "h-20 w-20 rounded-full flex items-center justify-center shadow-lg transition-all select-none " +
            (recording
              ? "bg-rose-500 hover:bg-rose-500 scale-105"
              : "bg-sky-500 hover:bg-sky-600 active:scale-95 disabled:bg-slate-300")
          }
          data-testid="tchat-mic-btn"
          aria-label={recording ? "Release to send" : "Press and hold to speak"}
        >
          {recording ? <Square className="h-7 w-7 text-white" /> : <Mic className="h-8 w-8 text-white" />}
        </button>
      </div>
      <div className="text-xs text-slate-500 text-center">
        {recording
          ? <span data-testid="tchat-recording-indicator">Listening… release to send</span>
          : (disabled ? "Wait for the tutor to finish…" : "Press and hold the mic to ask your doubt")}
      </div>
    </div>
  );
}

export function ChatComposer({ value, onChange, onSend, busy, image, onImage, onClearImage }) {
  const fileRef = useRef(null);
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };
  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large. Max 8 MB."); return; }
    const r = new FileReader();
    r.onload = () => onImage?.(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div>
      {image && (
        <div className="mb-2 inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 p-1.5 bg-white dark:bg-slate-800" data-testid="tchat-image-preview">
          <img src={image} alt="attachment" className="h-12 w-12 rounded-lg object-cover" />
          <button type="button" onClick={onClearImage} className="text-slate-400 hover:text-rose-500 pr-1" data-testid="tchat-image-remove"><X className="h-4 w-4" /></button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="tchat-image-input" />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="rounded-full h-11 w-11 p-0 shrink-0"
          title="Attach a photo"
          data-testid="tchat-attach"
        >
          <ImageIcon className="h-4 w-4" />
        </Button>
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask a doubt or attach a photo… (Enter to send)"
          rows={2}
          className="resize-none rounded-2xl"
          disabled={busy}
          data-testid="tchat-input"
        />
        <Button
          onClick={onSend}
          disabled={busy || (!value.trim() && !image)}
          className="rounded-full bg-sky-500 hover:bg-sky-600 h-11 px-5"
          data-testid="tchat-send"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

// Compact 5-option speed picker (0.75x / 0.9x / 1x / 1.15x / 1.35x). Stored
// as a number that we apply to <audio>.playbackRate. Kannada is harder to
// follow at default 1.0x for some students so 0.9x is the most useful step.
export function SpeedPicker({ speed, setSpeed }) {
  const options = [
    { v: 0.75, label: "0.75×" },
    { v: 0.9,  label: "0.9×"  },
    { v: 1.0,  label: "1×"    },
    { v: 1.15, label: "1.15×" },
    { v: 1.35, label: "1.35×" },
  ];
  const [open, setOpen] = useState(false);
  const current = options.find((o) => Math.abs(o.v - speed) < 0.01) || options[2];
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
        data-testid="tchat-speed-btn"
        title={`Voice speed: ${current.label}`}
      >
        <Gauge className="h-3.5 w-3.5 text-sky-500" />
        {current.label}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 min-w-[110px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg p-1" data-testid="tchat-speed-menu">
          {options.map((o) => (
            <button
              key={o.v}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setSpeed(o.v); setOpen(false); }}
              className={`w-full text-left text-xs px-3 py-1.5 rounded-lg transition-colors ${
                Math.abs(o.v - speed) < 0.01
                  ? "bg-sky-500 text-white"
                  : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
              }`}
              data-testid={`tchat-speed-opt-${String(o.v).replace(".", "_")}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * useTuitionSession — opens a backend tuition_sessions row for the current
 * lesson, ticks an active-time heartbeat every 20s while the tab is visible
 * AND the student isn't idle (>2 minutes since the last interaction), fires
 * an end-beacon on unload, and exposes `recordQuestion()` for chat sends.
 *
 * The backend caps each heartbeat to 30 seconds, so a misbehaving client
 * can't inflate the time. Idle detection uses pointermove/keydown/click/scroll.
 */
export function useTuitionSession(lessonId) {
  const sessionIdRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const HEARTBEAT_SECONDS = 20;
  const IDLE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

  useEffect(() => {
    if (!lessonId) return undefined;
    let cancelled = false;
    let timer = null;

    // Refresh "activity" on any interaction so the heartbeat keeps firing.
    const bumpActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ["pointermove", "pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bumpActivity, { passive: true }));

    (async () => {
      try {
        const { data } = await api.post("/tuition/session/start", { lesson_id: lessonId });
        if (cancelled) return;
        sessionIdRef.current = data.session_id;
        timer = setInterval(() => {
          const sid = sessionIdRef.current;
          if (!sid) return;
          if (document.visibilityState !== "visible") return;
          if (Date.now() - lastActivityRef.current > IDLE_THRESHOLD_MS) return;
          api.post("/tuition/session/heartbeat", {
            session_id: sid,
            delta_seconds: HEARTBEAT_SECONDS,
          }).catch(() => {/* silent — best-effort */});
        }, HEARTBEAT_SECONDS * 1000);
      } catch {
        /* session tracking is best-effort; never break the tutor experience */
      }
    })();

    // End-beacon — sendBeacon survives tab close + works during pagehide.
    const endSession = () => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/tuition/session/end`;
        // sendBeacon requires a Blob; we can't add Authorization headers, so
        // we fall back to fetch+keepalive (which is allowed during pagehide).
        const token = localStorage.getItem("sa_student_token") || "";
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ session_id: sid }),
          keepalive: true,
        }).catch(() => {/* noop */});
      } catch {/* noop */}
    };
    window.addEventListener("pagehide", endSession);
    window.addEventListener("beforeunload", endSession);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      events.forEach((e) => window.removeEventListener(e, bumpActivity));
      window.removeEventListener("pagehide", endSession);
      window.removeEventListener("beforeunload", endSession);
      endSession(); // also end when navigating within the SPA
    };
  }, [lessonId]);

  const recordQuestion = () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    api.post("/tuition/session/question", { session_id: sid }).catch(() => {/* noop */});
  };

  return { recordQuestion };
}
